import { useCallback, useState } from 'react';
import moment from 'moment';
import { ValuationApproachModel } from 'api';
import {
  ADJUSTMENT_25TH_PERCENTILE,
  ADJUSTMENT_75TH_PERCENTILE,
  ADJUSTMENT_MEAN,
  ADJUSTMENT_MEDIAN,
  ADJUSTMENT_SELECTION_A,
  ADJUSTMENT_SELECTION_B,
  SPECIFIED,
} from 'common/constants/valuations';
import { PERCENTILE } from 'common/formulas/math.js/PERCENTILE';
import { useFormat } from 'common/hooks';
import { useStore } from 'common/store';
import { Format } from 'common/types/format';
import { ValuationsApproach } from 'common/types/valuation';
import { compKeyMap } from 'pages/Valuations/approaches/backsolveApproach/MarketAdjustmentLedger/util/constants';
import { useRefreshHelpersWithContext } from 'pages/Valuations/components/RefreshGPCOption/hooks';
import { BacksolveAttributes } from 'pages/ValuationsAllocation/types';
import { useGetCompsByDate } from 'services/hooks/valuation';
import { ISO_DATE_FORMAT, Logger, toString } from 'utilities';
import { ValuationApproachBackSolveExtended } from '../types';

interface UseMarketAdjustmentTableChangeProps {
  gpcApproach: ValuationsApproach | undefined;
  approach: { valuations_approach_backsolve: ValuationApproachBackSolveExtended };
  setMarketAdjustment: (value: string) => void;
  backsolveAttributes?: BacksolveAttributes;
}

export const useMarketAdjustmentTableChange = ({
  gpcApproach,
  approach,
  setMarketAdjustment,
  backsolveAttributes,
}: UseMarketAdjustmentTableChangeProps) => {
  const [fetchGpcCompData] = useGetCompsByDate();
  const { getFlattenedComparisons } = useRefreshHelpersWithContext();
  const [{ firmId }] = useStore();
  const [format] = useFormat({ page: 'CAP_TABLE_CURRENCY_PAGE' }) as [Format, any];
  const [isLoadingMarketAdjustment, setIsLoadingMarketAdjustment] = useState(false);

  // Helper function to get metric value from company data
  const getMetricValue = useCallback((comp: any, metric: string) => {
    const metricKey = Object.keys(compKeyMap).find(key => compKeyMap[key as keyof typeof compKeyMap] === metric);
    return metricKey ? Number(comp[metricKey] || 0) : 0;
  }, []);

  const onMarketAdjustmentTableChange = useCallback(
    async (value: string | number) => {
      if (value && gpcApproach) {
        setIsLoadingMarketAdjustment(true);
        try {
          const asOfDateMoment = moment(value as string).format(ISO_DATE_FORMAT);
          const identifiers = (gpcApproach?.valuations_approach_gpc?.gpc_comparison ?? [])
            .map(comp => comp.ticker_symbol)
            .filter((ticker): ticker is string => Boolean(ticker));

          if (identifiers.length > 0 && format.currency.code) {
            const asOfCompsData = await fetchGpcCompData(
              identifiers,
              asOfDateMoment,
              format.currency.code as string,
              firmId,
              []
            );
            if (asOfCompsData?.results) {
              const flattenedComps = getFlattenedComparisons(asOfCompsData.results as any);
              // Update existing public company status entries with new data and calculate adjustments
              const existingStatuses = approach?.valuations_approach_backsolve?.public_companies_status || [];
              const gpcComparison = gpcApproach?.valuations_approach_gpc?.gpc_comparison || [];
              const metric = approach?.valuations_approach_backsolve?.metric;
              
              const updatedStatuses = existingStatuses.map((existingStatus: any) => {
                const matchingAsOfComp = flattenedComps.find((comp: any) => comp.cap_iq_id === existingStatus.cap_iq_id);
                const matchingMdComp = gpcComparison.find((comp: any) => comp.cap_iq_id === existingStatus.cap_iq_id);
                
                if (matchingAsOfComp && matchingMdComp && metric) {
                  const {
                    ltm_revenue: asOfLtmRevenue,
                    ltm_ebitda: asOfLtmEbitda,
                    ntm_revenue: asOfNtmRevenue,
                    ntm_ebitda: asOfNtmEbitda,
                    market_cap: asOfMarketCap,
                    enterprise_value: asOfEnterpriseValue,
                    ticker_symbol,
                  } = matchingAsOfComp;
                  
                  // Get the metric values for calculation
                  // Column B: As-of date data
                  const asOfMetricValue = getMetricValue(matchingAsOfComp, metric);
                  // Column C: Market date data (from GPC comparisons)
                  const mdMetricValue = getMetricValue(matchingMdComp, metric);
                  
                  // Calculate the percentage change: (C - B) / B
                  const percentageChange = asOfMetricValue !== 0 ? (mdMetricValue - asOfMetricValue) / asOfMetricValue : 0;
                  
                  return {
                    ...existingStatus,
                    ltm_revenue: toString(asOfLtmRevenue ?? 0),
                    ltm_ebitda: toString(asOfLtmEbitda ?? 0),
                    ntm_revenue: toString(asOfNtmRevenue ?? 0),
                    ntm_ebitda: toString(asOfNtmEbitda ?? 0),
                    market_cap: toString(asOfMarketCap ?? 0),
                    enterprise_value: toString(asOfEnterpriseValue ?? 0),
                    ticker_symbol: ticker_symbol ?? existingStatus.ticker_symbol,
                    // Store the calculated percentage change for the selected metric
                    percent_change: percentageChange,
                  };
                }
                
                return existingStatus;
              });

              // Calculate overall percentage change for all companies using the selected adjustment method
              const validPercentageChanges = updatedStatuses
                .filter((status: any) => (status as any).percent_change && status.enabled)
                .map((status: any) => (status as any).percent_change);
              
              const adjustmentSelected = approach?.valuations_approach_backsolve?.adjustment_selected;
              const percentileSelectionA = approach?.valuations_approach_backsolve?.percentile_selection_a;
              const percentileSelectionB = approach?.valuations_approach_backsolve?.percentile_selection_b;
              let overallPercentageChange = 0;
              
              if (validPercentageChanges.length > 0) {
                // Convert to string array for PERCENTILE function
                const percentageChangesAsStrings = validPercentageChanges.map((change: number) => change.toString());
                
                switch (adjustmentSelected) {
                  case ADJUSTMENT_MEAN:
                    overallPercentageChange = validPercentageChanges.reduce((sum: number, change: number) => sum + change, 0) / validPercentageChanges.length;
                    break;
                  case ADJUSTMENT_MEDIAN:
                    overallPercentageChange = PERCENTILE(percentageChangesAsStrings, 50);
                    break;
                  case ADJUSTMENT_SELECTION_A:
                    if (percentileSelectionA) {
                      overallPercentageChange = PERCENTILE(percentageChangesAsStrings, percentileSelectionA);
                    }
                    break;
                  case ADJUSTMENT_SELECTION_B:
                    if (percentileSelectionB) {
                      overallPercentageChange = PERCENTILE(percentageChangesAsStrings, percentileSelectionB);
                    }
                    break;
                  case ADJUSTMENT_25TH_PERCENTILE:
                    overallPercentageChange = PERCENTILE(percentageChangesAsStrings, 25);
                    break;
                  case ADJUSTMENT_75TH_PERCENTILE:
                    overallPercentageChange = PERCENTILE(percentageChangesAsStrings, 75);
                    break;
                  case SPECIFIED:
                    overallPercentageChange = Number(approach?.valuations_approach_backsolve?.market_adjustment ?? 0);
                    break;
                  default:
                    break;
                }
              }
              const marketAdjustmentDecimalPlaces = Number(backsolveAttributes?.market_adjustment?.decimal_places ?? 4);
              const newMarketAdjustment = Number(overallPercentageChange.toFixed(marketAdjustmentDecimalPlaces));
              // Update the approach with the updated public companies status
              // eslint-disable-next-line no-param-reassign
              approach.valuations_approach_backsolve.public_companies_status = updatedStatuses;
              // Update the marketAdjustment state to prevent reversion
              setMarketAdjustment(toString(newMarketAdjustment));
            }
          }
        } catch (error) {
          // Error handling for public company data fetching
          Logger.error('Error fetching public company data for market adjustment', error);
        } finally {
          setIsLoadingMarketAdjustment(false);
        }
      }
    },
    [gpcApproach, backsolveAttributes, format, fetchGpcCompData, firmId, getFlattenedComparisons, approach, getMetricValue, setMarketAdjustment]
  );

  return { onMarketAdjustmentTableChange, isLoadingMarketAdjustment };
};
